import type React from 'react';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import FolderIcon from '@atlaskit/icon/core/folder-closed';

import { Ati } from '@atlassian/ari';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.folder.title',
		defaultMessage: 'Folder',
		description: 'Title indicating that this option will create a folder',
	},
});

export function useFolderItem({
	parentId,
	source,
	spaceKey,
	onClick,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: 'create-folder-popover-item',
	});

	const createHref = buildCreationLink({
		contentType: 'folder',
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	});
	const hybridPageLinkClick = useHybridLinkClick(createHref);

	const handleClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			onClick('createFolderPopoverItem');
			hybridPageLinkClick(event);
		},
		[onClick, hybridPageLinkClick],
	);

	return {
		ati: Ati.parse('ati:cloud:confluence:folder'),
		children: formatMessage(messages.title),
		onClick: enforceStorageLimit(handleClick),
		elemBefore: FolderIcon,
		...(!shouldBlockCreate && { href: createHref }),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}
